import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useCampaignFiles } from '@/app/module/campaigns/component/campaign-files-provider'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { FileType, FileUploadType } from '@/app/types'

import { toCampaignFileError } from './campaign-file-error'
import {
  type CampaignFileUploadApiArg,
  getCampaignFileDownloadPath,
  getCampaignFileSignPathsApiArg,
  getCampaignFileUploadApiArg,
  getSignedCampaignFileUrl,
} from './campaign-file-url'

export type SaveCampaignFilePayload = {
  item: FileUploadType
  msgId: string
}

export const useSaveCampaignFile = () => {
  const dispatch = useAppDispatch()
  const { startLoading, saveFile: storeFile, finishLoading } = useCampaignFiles()
  const orgId = useSelector(selectOrgId)
  const [upload, uploadResponse] = esApi.endpoints.uploadFile.useMutation()
  const [signPaths, signPathsResponse] = esApi.endpoints.signPaths.useMutation()

  const saveFile = useCallback(
    async ({ item, msgId }: SaveCampaignFilePayload): Promise<FileType | Error> => {
      startLoading(msgId)

      try {
        const uploadCampaignFile = upload as (arg: CampaignFileUploadApiArg) => ReturnType<typeof upload>
        const uploaded = await uploadCampaignFile(getCampaignFileUploadApiArg({ item, orgId })).unwrap()
        const { filename, title } = uploaded
        const signedPath = getCampaignFileDownloadPath({ orgId, filename })
        const signedRes = await signPaths(getCampaignFileSignPathsApiArg(signedPath)).unwrap()
        const result = {
          id: msgId,
          filename,
          title,
          url: getSignedCampaignFileUrl({ signedPath, urlToken: signedRes[signedPath] }),
        }

        storeFile(result)

        return result
      } catch (error) {
        const message = getErrorMessage(error, 'Failed to upload file')

        finishLoading(msgId)
        dispatch(
          createNotification({
            'upload-file': {
              type: 'error',
              message,
            },
          }),
        )

        return toCampaignFileError(error, message)
      }
    },
    [orgId, dispatch, finishLoading, signPaths, startLoading, storeFile, upload],
  )

  return [
    saveFile,
    {
      ...uploadResponse,
      isLoading: uploadResponse.isLoading || signPathsResponse.isLoading,
      isUploading: uploadResponse.isLoading,
    },
  ] as const
}
